/*
  Timed email capture popup.

  Opens after a delay on first visit, then stays shut for the configured number
  of days once someone closes it or signs up. Dismissal is stored per browser in
  localStorage; if storage is unavailable the popup simply opens each session
  rather than erroring.
*/
class NewsletterPopup extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector('.newsletter-popup__dialog');
    this.storageKey = this.dataset.storageKey || 'newsletter-popup';
    this.suppressDays = parseInt(this.dataset.suppressDays, 10) || 30;
    this.delay = (parseInt(this.dataset.delay, 10) || 0) * 1000;
    this.lastFocused = null;
    this.timer = null;
  }

  connectedCallback() {
    this.querySelectorAll('[data-popup-close]').forEach((el) =>
      el.addEventListener('click', () => this.close(true))
    );

    const codeButton = this.querySelector('[data-copy-code]');
    if (codeButton) codeButton.addEventListener('click', () => this.copyCode(codeButton));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // A successful signup re-renders the page with the form posted, so show the
    // popup immediately to confirm it, then suppress future openings.
    if (this.querySelector('.newsletter-form__message--success')) {
      this.open();
      this.suppress();
      return;
    }

    if (this.isSuppressed()) return;
    this.startWhenVisible();
  }

  startWhenVisible() {
    if (document.visibilityState === 'hidden') {
      document.addEventListener('visibilitychange', this.onVisible = () => {
        if (document.visibilityState !== 'hidden') {
          document.removeEventListener('visibilitychange', this.onVisible);
          this.startWhenVisible();
        }
      });
      return;
    }
    this.timer = setTimeout(() => this.open(), this.delay);
  }

  disconnectedCallback() {
    if (this.timer) clearTimeout(this.timer);
    if (this.onVisible) document.removeEventListener('visibilitychange', this.onVisible);
  }

  copyCode(button) {
    const code = button.dataset.copyCode;
    const done = () => {
      button.classList.add('is-copied');
      setTimeout(() => button.classList.remove('is-copied'), 2000);
    };

    // Clipboard access is blocked in some contexts, so fall back to selecting
    // the code so it can still be copied by hand.
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(code).then(done).catch(() => this.selectCode(button));
    } else {
      this.selectCode(button);
    }
  }

  selectCode(button) {
    const value = button.querySelector('.newsletter-popup__code-value');
    if (!value || !window.getSelection) return;
    const range = document.createRange();
    range.selectNodeContents(value);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  }

  isSuppressed() {
    try {
      const until = window.localStorage.getItem(this.storageKey);
      return !!until && Date.now() < parseInt(until, 10);
    } catch (e) {
      return false;
    }
  }

  suppress() {
    try {
      const until = Date.now() + this.suppressDays * 24 * 60 * 60 * 1000;
      window.localStorage.setItem(this.storageKey, String(until));
    } catch (e) {
      /* private mode or blocked storage - nothing to do */
    }
  }

  open() {
    this.lastFocused = document.activeElement;
    this.removeAttribute('hidden');
    // Force a reflow so the transition has a starting state. rAF would be
    // cleaner but never fires in a background tab, which previously left the
    // popup visible-but-transparent with the page scroll locked.
    void this.offsetWidth;
    this.classList.add('newsletter-popup--open');
    document.body.classList.add('overflow-hidden');

    const field = this.querySelector('input[type="email"]');
    (field || this.dialog).focus({ preventScroll: true });
  }

  close(persist) {
    if (persist) this.suppress();
    this.classList.remove('newsletter-popup--open');
    document.body.classList.remove('overflow-hidden');

    const finish = () => {
      this.setAttribute('hidden', '');
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus({ preventScroll: true });
      }
    };

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      finish();
    } else {
      setTimeout(finish, 200);
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close(true);
      return;
    }
    if (event.key !== 'Tab') return;

    // Keep focus inside the dialog while it is open.
    const focusable = this.dialog.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
